import { Markdown } from '@kanbu/chatbot-ui';
import { Trans } from '@lingui/react/macro';
import { Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import {
  memo,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';

interface DisclaimerAcknowledgementProps {
  disclaimerContent?: string;
  requireAcknowledgement?: boolean;
  onAcknowledgementChange?: (acknowledged: boolean) => void;
  onFocusRequest?: () => void;
}

export interface DisclaimerAcknowledgementRef {
  focus: () => void;
  scrollIntoView: () => void;
  triggerAttentionAnimation: () => void;
}

const LOCALSTORAGE_KEY = 'kanbu-disclaimer-acknowledged';

export const DisclaimerAcknowledgement = memo(
  forwardRef<DisclaimerAcknowledgementRef, DisclaimerAcknowledgementProps>(
    (
      {
        disclaimerContent,
        requireAcknowledgement,
        onAcknowledgementChange,
        onFocusRequest,
      },
      ref,
    ) => {
      const [disclaimerAcknowledged, setDisclaimerAcknowledged] =
        useState(false);
      const [showAttentionAnimation, setShowAttentionAnimation] =
        useState(false);
      const [showAcknowledgementAnimation, setShowAcknowledgementAnimation] =
        useState(false);
      const checkboxRef = useRef<HTMLDivElement>(null);
      const checkboxInputRef = useRef<HTMLInputElement>(null);
      const [utmParams, setUtmParams] = useState(new URLSearchParams());

      useEffect(() => {
        setUtmParams(
          new URLSearchParams({
            utm_medium: 'web',
            utm_source: window.location.host,
          }),
        );
      }, []);

      useEffect(() => {
        if (requireAcknowledgement) {
          const acknowledged =
            localStorage.getItem(LOCALSTORAGE_KEY) === 'true';
          setDisclaimerAcknowledged(acknowledged);
          onAcknowledgementChange?.(acknowledged);
        } else {
          setDisclaimerAcknowledged(true);
          onAcknowledgementChange?.(true);
        }
      }, [requireAcknowledgement, onAcknowledgementChange]);

      const handleAcknowledge = () => {
        setDisclaimerAcknowledged(true);
        localStorage.setItem(LOCALSTORAGE_KEY, 'true');
        setShowAcknowledgementAnimation(true);
        onAcknowledgementChange?.(true);

        // After celebration animation, focus the input
        setTimeout(() => {
          setShowAcknowledgementAnimation(false);
          onFocusRequest?.();
        }, 1000);
      };

      // Expose methods to parent component
      useImperativeHandle(ref, () => ({
        focus: () => {
          checkboxInputRef.current?.focus();
        },
        scrollIntoView: () => {
          checkboxRef.current?.scrollIntoView({ behavior: 'smooth' });
        },
        triggerAttentionAnimation: () => {
          setShowAttentionAnimation(true);
          setTimeout(() => setShowAttentionAnimation(false), 300);
        },
      }));

      return (
        <div
          className='flex flex-col items-center justify-between gap-2'
          data-testid='disclaimer-acknowledgement'
        >
          <div className='w-full text-center text-xs text-chat-muted-foreground'>
            <Trans>Content is generated using AI. More on</Trans>&nbsp;
            <a
              className='underline'
              href={`https://kanbu.ai?${utmParams.toString()}`}
              target='_blank'
              rel='noreferrer'
            >
              <strong>Kanbu.ai</strong>
            </a>
            {disclaimerContent && <Markdown>{disclaimerContent}</Markdown>}
          </div>

          {/* Show checkbox if disclosure needed and not yet acknowledged */}
          {requireAcknowledgement && !disclaimerAcknowledged && (
            <div ref={checkboxRef} className='w-full'>
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 1, scale: 1 }}
                  animate={
                    showAttentionAnimation ? { scale: 1.1 } : { scale: 1 }
                  }
                  className='bg-chat-accent/10 flex items-center justify-center rounded-md p-2'
                >
                  <label className='flex cursor-pointer items-center gap-2 text-sm'>
                    <input
                      ref={checkboxInputRef}
                      type='checkbox'
                      checked={disclaimerAcknowledged}
                      onChange={() => handleAcknowledge()}
                      className='size-4 accent-chat-primary'
                    />
                    <span className='text-chat-foreground'>
                      <Trans>I acknowledge the disclaimer</Trans>
                    </span>
                  </label>
                </motion.div>
              </AnimatePresence>
            </div>
          )}

          {/* Celebration animation overlay */}
          <AnimatePresence>
            {showAcknowledgementAnimation && (
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 1.5, opacity: 0 }}
                className='pointer-events-none fixed inset-0 z-50 flex items-center justify-center'
              >
                <motion.div
                  className='rounded-full bg-chat-primary p-3 text-chat-primary-foreground'
                  animate={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  <Check className='size-10' />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    },
  ),
);
